import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ExternalLink, Check, X, Undo2, Search, AlertTriangle } from "lucide-react";
import { DuesPanel } from "@/pages/customer/Payments";

// Staff: verification queue for bank-transfer proofs (UTR + screenshot) and a per-customer dues view.
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TABS = [["PENDING_VERIFY", "To verify"], ["VERIFIED", "Verified"], ["REJECTED", "Rejected"], ["REVERSED", "Reversed"], ["ALL", "All"]];

function ProofCard({ r, isAdmin, onChanged }) {
  const [alloc, setAlloc] = useState(() => Object.fromEntries((r.suggested?.allocations || []).map((a) => [a.invoice_number, { cash: a.cash, tds: a.tds }])));
  const [busy, setBusy] = useState(false);
  const total = Object.values(alloc).reduce((s, a) => s + Number(a.cash || 0), 0);
  const view = async () => {
    try { const x = await api.get(`/v2/dues/receipts/${r.id}/proof`); window.open(x.data.url, "_blank", "noopener,noreferrer"); }
    catch (e) { toast.error(e.response?.data?.detail || "No proof"); }
  };
  const act = async (kind) => {
    let body = {};
    if (kind === "verify") {
      if (total > r.amount + 0.01) { toast.error("Allocated more than received"); return; }
      if (!window.confirm(`Verify ${fmt(r.amount)} (UTR ${r.utr}) as RECEIVED in the bank?\n\nOnly confirm after you have seen this UTR in the bank statement.`)) return;
      body = { allocations: Object.entries(alloc).filter(([, a]) => Number(a.cash) > 0 || Number(a.tds) > 0).map(([n, a]) => ({ invoice_number: n, cash: Number(a.cash || 0), tds: Number(a.tds || 0) })) };
    } else {
      const reason = window.prompt(kind === "reject" ? "Why is it not accepted? (the customer sees this)" : "Reason for reversing this verified payment?");
      if (!reason || !reason.trim()) return;
      body = { reason: reason.trim() };
    }
    setBusy(true);
    try { await api.post(`/v2/dues/receipts/${r.id}/${kind}`, body); toast.success(kind === "verify" ? "Verified - invoices updated" : kind === "reject" ? "Rejected" : "Reversed"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`proof-${r.receipt_no}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono text-xs text-slate-500">{r.receipt_no}</span>
        <span className="font-semibold text-slate-900">{r.customer_name || r.customer_id}</span>
        <span className="text-lg font-bold tabular-nums">{fmt(r.amount)}</span>
        <span className="text-sm text-slate-600">UTR <b className="font-mono">{r.utr}</b> · {String(r.paid_on || "").slice(0, 10)}{r.tds_percent ? ` · TDS ${r.tds_percent}%` : ""}</span>
        <span className="text-xs text-slate-400">by {r.uploaded_by?.role === "customer" ? "customer" : (r.uploaded_by?.email || r.uploaded_by?.role)}</span>
        {r.has_proof ? <button onClick={view} className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-4 h-4" />View screenshot</button> : <span className="text-xs text-slate-400">no screenshot</span>}
      </div>
      {r.note && <p className="text-xs text-slate-500 mt-1">Note: {r.note}</p>}
      {r.duplicate_utr_warning && <p className="text-xs text-red-700 mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r.duplicate_utr_warning}</p>}
      {r.status === "PENDING_VERIFY" && (<>
        {(r.open_invoices || []).length === 0 ? <p className="text-xs text-slate-500 mt-3">This customer has no open invoices; the whole amount will be kept as advance.</p> : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm min-w-[520px]"><thead className="text-[11px] uppercase text-slate-500"><tr><th className="text-left py-1">Invoice</th><th className="text-right">Due</th><th className="text-right">Apply cash</th><th className="text-right">TDS</th></tr></thead>
              <tbody>{r.open_invoices.map((i) => {
                const a = alloc[i.invoice_number] || { cash: 0, tds: 0 };
                return (<tr key={i.invoice_number} className="border-t border-slate-100">
                  <td className="py-1 font-mono text-xs">{i.invoice_number}</td><td className="text-right tabular-nums">{fmt(i.outstanding)}</td>
                  <td className="text-right"><input type="number" step="0.01" value={a.cash} disabled={!isAdmin} onChange={(e) => setAlloc((m) => ({ ...m, [i.invoice_number]: { ...a, cash: e.target.value } }))} className="w-28 h-8 rounded border border-slate-300 px-2 text-right text-sm" /></td>
                  <td className="text-right"><input type="number" step="0.01" value={a.tds} disabled={!isAdmin} onChange={(e) => setAlloc((m) => ({ ...m, [i.invoice_number]: { ...a, tds: e.target.value } }))} className="w-24 h-8 rounded border border-slate-300 px-2 text-right text-sm" /></td></tr>);
              })}</tbody></table>
            <p className={`text-xs mt-1 ${total > r.amount + 0.01 ? "text-red-700" : "text-slate-500"}`}>Allocated {fmt(total)} of {fmt(r.amount)} · advance {fmt(Math.max(r.amount - total, 0))}</p>
          </div>)}
        {isAdmin ? (
          <div className="flex gap-2 mt-3">
            <button disabled={busy} onClick={() => act("verify")} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"><Check className="w-4 h-4" />Verify received</button>
            <button disabled={busy} onClick={() => act("reject")} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-red-300 text-red-700 text-sm font-semibold disabled:opacity-50"><X className="w-4 h-4" />Reject</button>
          </div>) : <p className="text-xs text-slate-500 mt-3">An admin verifies this against the bank statement.</p>}
      </>)}
      {r.status === "VERIFIED" && (
        <div className="mt-2 text-xs text-slate-600 flex flex-wrap items-center gap-2">
          <span>{(r.allocations || []).map((a) => `${a.invoice_number} ${fmt(a.cash)}${a.tds ? ` + TDS ${fmt(a.tds)}` : ""}`).join(" · ") || "no invoices"}{r.unallocated > 0.5 ? ` · advance ${fmt(r.unallocated)}` : ""}</span>
          <span className="text-slate-400">verified by {r.verified_by?.email}</span>
          {isAdmin && r.source !== "LINK" && <button disabled={busy} onClick={() => act("undo")} className="ml-auto inline-flex items-center gap-1 text-red-700 hover:underline"><Undo2 className="w-3 h-3" />Undo</button>}
        </div>)}
      {r.status === "REJECTED" && <p className="mt-2 text-xs text-red-700">Rejected: {r.reject_reason}</p>}
    </div>
  );
}

export default function PaymentProofs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("PENDING_VERIFY");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [cust, setCust] = useState(null);

  const load = async () => {
    setRows(null);
    try { const r = await api.get("/v2/dues/receipts", { params: { status: tab } }); setRows(r.data.items); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load"); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  useEffect(() => {
    if (q.trim().length < 2) { setOpts([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/v2/customer-profiles/search?q=${encodeURIComponent(q.trim())}`); setOpts(r.data?.results || []); } catch { setOpts([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <DashboardLayout title="Payment proofs">
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="payment-proofs-page">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Payments & proofs</h1>
          <p className="text-sm text-slate-500">Bank transfers reported by customers (or by staff) wait here until an admin checks the UTR in the bank statement.</p>
        </div>
        <div className="flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500"}`}>{l}</button>)}
        </div>
        {rows === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">Nothing here.</p>
          : <div className="space-y-3">{rows.map((r) => <ProofCard key={r.id} r={r} isAdmin={isAdmin} onChanged={load} />)}</div>}

        <div className="pt-4 border-t border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-2">Customer dues · send a pay link or record a transfer</h2>
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-2.5 top-3 text-slate-400" />
            <input className="w-full h-10 rounded-md border border-slate-300 pl-8 pr-3 text-sm" placeholder="Search customer by name, phone or GSTIN" value={q} onChange={(e) => setQ(e.target.value)} />
            {opts.length > 0 && <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow max-h-64 overflow-auto">{opts.map((o) => (
              <li key={o.customer_id}><button className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => { setCust(o); setOpts([]); setQ(o.company_name || o.contact_person || ""); }}>
                {o.company_name || o.contact_person} <span className="text-xs text-slate-400">{o.customer_code} {o.mobile} {o.gst_number}</span></button></li>))}</ul>}
          </div>
          {cust && <div className="mt-4"><DuesPanel key={cust.customer_id} customerId={cust.customer_id} staff /></div>}
        </div>
      </div>
    </DashboardLayout>
  );
}
